import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';
import { getAdminToken } from '../adminSession';
import '../AdminDetailOverview.scss';
import '../../screens/pastor/Pastor.scss';
import './PastorCallAdmin.scss';
import PastorProfileView from '../../screens/pastor/components/PastorProfileView';
import { DELIVERY_STATUS_LABEL, REQUEST_STATUS_LABEL, saveBlob } from '../../screens/pastor/pastorApi';
import type { CallRequest, DeliveryStatus, PastorFile, PastorProfile, RequestStatus } from '../../screens/pastor/pastorApi';
import { DENOMINATIONS, RURAL_LABEL } from '../../screens/pastor/pastorOptions';

// 담임청빙 중개 관리 — 서버 /pastorcalladmin (routers/pastor/PastorAdmin.js)
const api = axios.create({ baseURL: `${MainURL}/pastorcalladmin` });
api.interceptors.request.use((config) => {
  const token = getAdminToken();
  if (token) config.headers.set('X-Admin-Token', token);
  return config;
});
const err = (e: unknown, fallback = '처리하지 못했습니다.') => (e as { response?: { data?: { error?: string } } })?.response?.data?.error || fallback;
// 사역자 구직 서류(자기소개서 등) 내려받기
const openFile = (f: PastorFile) =>
  api.get(`/pastor-files/${f.id}`, { responseType: 'blob' }).then((r) => saveBlob(r.data, f.name)).catch(() => alert('서류를 내려받지 못했습니다.'));

export type PastorCallTab = 'requests' | 'pastors' | 'email' | 'promotions' | 'consults';

type AdminRequest = CallRequest & {
  id: number;
  source: 'FORM' | 'RECRUIT';
  status: RequestStatus;
  createdAt: string;
  adminMemo: string;
  recruitPath: string;
  deliveryCounts?: Partial<Record<DeliveryStatus, number>>;
};
type Candidate = PastorProfile & { id: number; score: number; reasons: string[]; unmetNeeds: string[] };
type Delivery = {
  id: number; origin: 'ADMIN' | 'PASTOR'; status: DeliveryStatus; adminMessage: string; pastorNote: string;
  createdAt: string; expiresAt: string | null; deliveredAt: string | null; churchViewedAt: string | null; viewPath: string; pastor: PastorProfile | null;
};

const summary = (p: PastorProfile) => [p.ageBand, p.denomination, p.currentRole, p.ministryYears != null ? `사역 ${p.ministryYears}년` : ''].filter(Boolean).join(' · ');

// 탭은 관리자 사이드바의 '담임청빙 중개' 하위 메뉴로 선택한다(Admin/Main.tsx).
export default function PastorCallAdmin({ tab, onShowRequests }: { tab: PastorCallTab; onShowRequests: () => void }) {
  const [openRequestId, setOpenRequestId] = useState<number | null>(null);

  useEffect(() => {
    if (tab !== 'requests') setOpenRequestId(null);
  }, [tab]);

  return (
    <div className="service-detail-overview pastor-admin">
      <p className="service-detail-overview__hint">
        담임목회 희망 이력서와 교회의 청빙 의뢰를 잇습니다. 의뢰마다 맞는 사역자에게 동의를 요청하면, 사역자가 동의하는 즉시 교회 담당자에게 이력서가 전달됩니다.
      </p>
      {tab === 'requests' && (openRequestId
        ? <RequestDetail id={openRequestId} onBack={() => setOpenRequestId(null)} />
        : <RequestList onOpen={setOpenRequestId} />)}
      {tab === 'pastors' && <PastorList />}
      {tab === 'email' && <MailPromo />}
      {tab === 'promotions' && <Promotions onOpenRequest={(id) => { setOpenRequestId(id); onShowRequests(); }} />}
      {tab === 'consults' && <Consults />}
    </div>
  );
}

// ---------------------------------------------------------------------------------
// 청빙 의뢰
// ---------------------------------------------------------------------------------
function RequestList({ onOpen }: { onOpen: (id: number) => void }) {
  const [status, setStatus] = useState('');
  const [items, setItems] = useState<AdminRequest[] | null>(null);

  useEffect(() => {
    setItems(null);
    api.get('/requests', { params: { status } }).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, [status]);

  return (
    <>
      <div className="service-detail-overview__tabs">
        {[['', '전체'], ...Object.entries(REQUEST_STATUS_LABEL)].map(([k, l]) => (
          <button key={k} type="button" className={`service-detail-overview__tab${status === k ? ' is-active' : ''}`} onClick={() => setStatus(k)}>{l}</button>
        ))}
      </div>
      {!items ? <div className="service-detail-overview__empty">불러오는 중...</div> : items.length === 0 ? (
        <div className="service-detail-overview__empty">청빙 의뢰가 없습니다. 담임 공고로 의뢰를 만들려면 '공고 홍보' 탭에서 [의뢰로 만들기]를 누르세요.</div>
      ) : (
        <div className="service-detail-overview__table-x">
          <table className="service-detail-overview__table">
            <thead><tr><th>접수일</th><th>교회</th><th>지역</th><th>출처</th><th>담당자</th><th>상태</th><th>진행</th></tr></thead>
            <tbody>
              {items.map((r) => {
                const c = r.deliveryCounts || {};
                return (
                  <tr key={r.id} className="service-detail-overview__table-row" onClick={() => onOpen(r.id)} style={{ cursor: 'pointer' }}>
                    <td>{r.createdAt?.slice(0, 10)}</td>
                    <td><b>{r.churchName}</b></td>
                    <td>{[r.sido, r.sigungu].filter(Boolean).join(' ')} {r.regionType && `(${r.regionType})`}</td>
                    <td>{r.source === 'RECRUIT' ? '담임 공고' : '의뢰서'}</td>
                    <td>{r.managerName} {r.managerPhone || <span className="pastor-admin__warn">연락처 없음</span>}</td>
                    <td>{REQUEST_STATUS_LABEL[r.status]}</td>
                    <td>
                      {c.INTERESTED ? <span className="pastor-admin__warn">관심 {c.INTERESTED} · </span> : null}
                      대기 {c.CONSENT_PENDING || 0} · 전달 {c.DELIVERED || 0}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function RequestDetail({ id, onBack }: { id: number; onBack: () => void }) {
  const [data, setData] = useState<{ request: AdminRequest; deliveries: Delivery[]; candidates: Candidate[] } | null>(null);
  const [picked, setPicked] = useState<number[]>([]);
  const [message, setMessage] = useState('');
  const [openPastor, setOpenPastor] = useState<number | null>(null);
  const [memo, setMemo] = useState('');
  const [contact, setContact] = useState({ managerName: '', managerPhone: '', managerEmail: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/requests/${id}`).then((r) => {
      setData(r.data);
      setMemo(r.data.request.adminMemo || '');
      setContact({ managerName: r.data.request.managerName, managerPhone: r.data.request.managerPhone, managerEmail: r.data.request.managerEmail });
    }).catch((e) => alert(err(e)));
  }, [id]);
  useEffect(load, [load]);

  const run = async (fn: () => Promise<unknown>, done?: string) => {
    setBusy(true);
    try {
      await fn();
      if (done) alert(done);
      load();
    } catch (e) {
      alert(err(e));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="service-detail-overview__empty">불러오는 중...</div>;
  const r = data.request;

  const patch = (body: Record<string, unknown>) => run(() => api.patch(`/requests/${id}`, body), '저장했습니다.');
  const sendConsent = () => {
    if (!picked.length) return alert('추천할 사역자를 선택해 주세요.');
    if (!window.confirm(`선택한 ${picked.length}명에게 '${r.churchName}' 이력서 전달 동의를 요청할까요? 앱 푸시와 이메일이 바로 발송됩니다.`)) return;
    run(async () => {
      const res = await api.post(`/requests/${id}/consent`, { pastorIds: picked, message });
      setPicked([]);
      alert(`${res.data.sent}명에게 동의 요청을 보냈습니다.`);
    });
  };

  return (
    <div className="pastor-admin__detail">
      <div className="service-detail-overview__toolbar">
        <button type="button" onClick={onBack}>← 목록</button>
        <b style={{ fontSize: 20 }}>{r.churchName}</b>
        <span>{REQUEST_STATUS_LABEL[r.status]}</span>
        <select value={r.status} onChange={(e) => patch({ status: e.target.value })}>
          {Object.entries(REQUEST_STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <label className="pastor-admin__check">
          <input type="checkbox" checked={r.allowPromote} onChange={(e) => patch({ allowPromote: e.target.checked })} /> 등록 사역자에게 홍보 허용
        </label>
      </div>

      <div className="pastor-admin__cols">
        <section>
          <h4>의뢰 내용</h4>
          <dl className="pastor-admin__dl">
            <div><dt>출처</dt><dd>{r.source === 'RECRUIT' ? <a href={r.recruitPath} target="_blank" rel="noreferrer">구인정보 담임 공고</a> : '청빙 의뢰서'}</dd></div>
            <div><dt>교단</dt><dd>{r.denomination || '-'}</dd></div>
            <div><dt>지역</dt><dd>{[r.sido, r.sigungu, r.regionType].filter(Boolean).join(' · ') || '-'} {r.address}</dd></div>
            <div><dt>규모</dt><dd>{r.attendance || '-'} {r.foundedYear ? `· ${r.foundedYear}년 설립` : ''}</dd></div>
            <div><dt>제공 조건</dt><dd>{r.offerConditions.join(', ') || '-'} {r.salaryNote && `/ ${r.salaryNote}`}</dd></div>
            <div><dt>교회 소개</dt><dd className="pc-pre">{r.intro || '-'}</dd></div>
            <div><dt>원하는 목사님</dt><dd className="pc-pre">{r.desiredNote || '-'}</dd></div>
          </dl>
        </section>
        <section>
          <h4>교회 담당자 (전달받을 곳)</h4>
          <div className="pastor-admin__form">
            <input value={contact.managerName} onChange={(e) => setContact({ ...contact, managerName: e.target.value })} placeholder="담당자 이름" />
            <input value={contact.managerPhone} onChange={(e) => setContact({ ...contact, managerPhone: e.target.value })} placeholder="휴대전화" />
            <input value={contact.managerEmail} onChange={(e) => setContact({ ...contact, managerEmail: e.target.value })} placeholder="이메일 (이력서 전달)" />
            <button type="button" disabled={busy} onClick={() => patch({ fields: { ...r, ...contact } })}>담당자 저장</button>
          </div>
          <h4>관리자 메모</h4>
          <textarea value={memo} onChange={(e) => setMemo(e.target.value)} rows={4} placeholder="통화 내용, 교회 요청 사항 등" />
          <button type="button" disabled={busy} onClick={() => patch({ adminMemo: memo })}>메모 저장</button>
        </section>
      </div>

      <h4>진행 현황 ({data.deliveries.length})</h4>
      {data.deliveries.length === 0 ? <div className="service-detail-overview__empty">아직 추천한 사역자가 없습니다.</div> : (
        <div className="service-detail-overview__table-x">
          <table className="service-detail-overview__table">
            <thead><tr><th>사역자</th><th>경로</th><th>상태</th><th>날짜</th><th>교회 열람</th><th>메모</th><th /></tr></thead>
            <tbody>
              {data.deliveries.map((d) => (
                <tr key={d.id}>
                  <td><b>{d.pastor?.realName || '삭제됨'}</b> {d.pastor && <small>{summary(d.pastor)}</small>}</td>
                  <td>{d.origin === 'ADMIN' ? '관리자 추천' : '사역자 관심 표시'}</td>
                  <td>{DELIVERY_STATUS_LABEL[d.status]}{d.status === 'CONSENT_PENDING' && d.expiresAt ? ` (~${d.expiresAt.slice(5, 10)})` : ''}</td>
                  <td>{(d.deliveredAt || d.createdAt)?.slice(0, 10)}</td>
                  <td>{d.churchViewedAt ? d.churchViewedAt.slice(0, 10) : '-'}</td>
                  <td className="pc-pre">{d.pastorNote || d.adminMessage || ''}</td>
                  <td className="pastor-admin__actions">
                    {d.status === 'INTERESTED' && <button type="button" disabled={busy} onClick={() => window.confirm('이 사역자의 이력서를 교회에 전달할까요?') && run(() => api.post(`/deliveries/${d.id}/deliver`), '전달했습니다.')}>교회에 전달</button>}
                    {['INTERESTED', 'CONSENT_PENDING'].includes(d.status) && <button type="button" disabled={busy} onClick={() => run(() => api.post(`/deliveries/${d.id}/cancel`))}>취소</button>}
                    {d.viewPath && <a href={d.viewPath} target="_blank" rel="noreferrer">전달된 이력서</a>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h4>추천 후보 ({data.candidates.length}) <small>적합도 순 · 추천 중지·제외 교회 지정한 분은 빠져 있습니다</small></h4>
      <div className="pastor-admin__send">
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2} placeholder="사역자에게 보낼 추천 사유 (선택) — 예) 농촌 교회 사역 소명과 교단이 맞아 추천드립니다." />
        <button type="button" className="is-primary" disabled={busy || !picked.length} onClick={sendConsent}>선택한 {picked.length}명에게 동의 요청</button>
      </div>
      {data.candidates.length === 0 ? <div className="service-detail-overview__empty">추천할 수 있는 등록 사역자가 없습니다.</div> : (
        <div className="service-detail-overview__table-x">
          <table className="service-detail-overview__table">
            <thead><tr><th /><th>점수</th><th>사역자</th><th>지방·농어촌</th><th>희망 지역</th><th>맞는 점</th><th>못 맞추는 조건</th><th /></tr></thead>
            <tbody>
              {data.candidates.map((c) => (
                <Fragment key={c.id}>
                  <tr>
                    <td><input type="checkbox" checked={picked.includes(c.id)} onChange={(e) => setPicked(e.target.checked ? [...picked, c.id] : picked.filter((x) => x !== c.id))} /></td>
                    <td><b>{c.score}</b></td>
                    <td><b>{c.realName}</b> <small>{summary(c)}</small></td>
                    <td>{c.ruralCalling ? RURAL_LABEL[c.ruralCalling] : '-'}</td>
                    <td>{[...c.hopeRegionTypes, ...c.hopeSidos].join(', ') || '무관'}</td>
                    <td>{c.reasons.join(', ') || '-'}</td>
                    <td className="pastor-admin__warn">{c.unmetNeeds.join(', ')}</td>
                    <td><button type="button" onClick={() => setOpenPastor(openPastor === c.id ? null : c.id)}>{openPastor === c.id ? '닫기' : '이력서'}</button></td>
                  </tr>
                  {openPastor === c.id && (
                    <tr><td colSpan={8}><PastorProfileView profile={c} onOpenFile={openFile} /></td></tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------
// 등록 사역자
// ---------------------------------------------------------------------------------
function PastorList() {
  const [keyword, setKeyword] = useState('');
  const [rural, setRural] = useState('');
  const [items, setItems] = useState<(PastorProfile & { id: number })[] | null>(null);
  const [open, setOpen] = useState<number | null>(null);

  const load = useCallback(() => {
    setItems(null);
    api.get('/pastors', { params: { keyword, rural } }).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, [keyword, rural]);
  useEffect(() => { load(); }, [rural]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className="service-detail-overview__toolbar">
        <input value={keyword} onChange={(e) => setKeyword(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} placeholder="이름·연락처·교단·교회·목회 철학" className="pastor-admin__search" />
        <select value={rural} onChange={(e) => setRural(e.target.value)}>
          <option value="">지방·농어촌 전체</option>
          <option value="YES">적극 희망</option>
          <option value="OPEN">가능 이상</option>
        </select>
        <button type="button" onClick={load}>검색</button>
        <span className="service-detail-overview__hint" style={{ margin: 0 }}>{items ? `${items.length}명` : ''}</span>
      </div>
      {!items ? <div className="service-detail-overview__empty">불러오는 중...</div> : items.length === 0 ? <div className="service-detail-overview__empty">등록 사역자가 없습니다.</div> : (
        <div className="service-detail-overview__table-x">
          <table className="service-detail-overview__table">
            <thead><tr><th>이름</th><th>연락처</th><th>요약</th><th>지방·농어촌</th><th>희망 지역</th><th>부임</th><th>상태</th><th>수정일</th><th /></tr></thead>
            <tbody>
              {items.map((p) => (
                <Fragment key={p.id}>
                  <tr>
                    <td><b>{p.realName}</b>{(p.files?.length ?? 0) > 0 && <><br /><small>서류 {p.files!.length}개</small></>}</td>
                    <td>{p.phone}<br /><small>{p.email}</small></td>
                    <td>{summary(p)}</td>
                    <td>{p.ruralCalling ? RURAL_LABEL[p.ruralCalling] : '-'}</td>
                    <td>{[...p.hopeRegionTypes, ...p.hopeSidos].join(', ') || '무관'}</td>
                    <td>{p.availableFrom || '-'}</td>
                    <td>{p.isActive ? '추천 받는 중' : <span className="pastor-admin__warn">일시중지</span>}</td>
                    <td>{p.updatedAt?.slice(0, 10)}</td>
                    <td><button type="button" onClick={() => setOpen(open === p.id ? null : p.id)}>{open === p.id ? '닫기' : '이력서'}</button></td>
                  </tr>
                  {open === p.id && <tr><td colSpan={9}><PastorProfileView profile={p} onOpenFile={openFile} /></td></tr>}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------------
// 공고 홍보
// ---------------------------------------------------------------------------------
type Source = { sourceType: 'RECRUIT' | 'REQUEST'; recruitType: string; id: number; title: string; church: string; region: string; path: string; postedAt: string; deadline: string; sent: { count: number; last: string } | null };
type Filters = { rural: string; sido: string; denominations: string[] };

function Promotions({ onOpenRequest }: { onOpenRequest: (id: number) => void }) {
  const [sources, setSources] = useState<{ recruits: Source[]; requests: Source[] } | null>(null);
  const [history, setHistory] = useState<{ id: number; sourceType: string; title: string; church: string; region: string; recipientCount: number; message: string; createdAt: string }[]>([]);
  const [target, setTarget] = useState<Source | null>(null);
  const [filters, setFilters] = useState<Filters>({ rural: '', sido: '', denominations: [] });
  const [preview, setPreview] = useState<{ id: number; realName: string; ageBand: string; denomination: string }[] | null>(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get('/promotions/sources').then((r) => setSources(r.data)).catch(() => setSources({ recruits: [], requests: [] }));
    api.get('/promotions').then((r) => setHistory(r.data.items)).catch(() => setHistory([]));
  }, []);
  useEffect(load, [load]);

  useEffect(() => {
    if (!target) return;
    setPreview(null);
    api.post('/promotions/preview', { filters }).then((r) => setPreview(r.data.items)).catch(() => setPreview([]));
  }, [target, filters]);

  const choose = (s: Source) => {
    setTarget(s);
    setFilters({ rural: '', sido: s.region.split(' ')[0] || '', denominations: [] });
    setMessage('');
  };

  const toRequest = async (s: Source) => {
    try {
      const res = await api.post('/requests/from-recruit', { recruitType: s.recruitType, recruitId: s.id });
      onOpenRequest(res.data.requestId);
    } catch (e) {
      alert(err(e));
    }
  };

  const send = async () => {
    if (!target || !preview?.length) return;
    if (!window.confirm(`'${target.title}'을(를) ${preview.length}명에게 홍보 발송할까요?`)) return;
    setBusy(true);
    try {
      const res = await api.post('/promotions', { sourceType: target.sourceType, recruitType: target.recruitType, sourceId: target.id, message, filters });
      alert(`${res.data.count}명에게 발송을 시작했습니다.`);
      setTarget(null);
      load();
    } catch (e) {
      alert(err(e));
    } finally {
      setBusy(false);
    }
  };

  if (!sources) return <div className="service-detail-overview__empty">불러오는 중...</div>;
  const rows = [...sources.requests, ...sources.recruits];

  return (
    <>
      {target && (
        <div className="pastor-admin__promo">
          <h4>홍보 발송 — {target.title} <small>{target.church} · {target.region}</small></h4>
          <div className="service-detail-overview__toolbar">
            <select value={filters.rural} onChange={(e) => setFilters({ ...filters, rural: e.target.value })}>
              <option value="">지방·농어촌 무관</option>
              <option value="YES">적극 희망만</option>
              <option value="OPEN">가능 이상</option>
            </select>
            <input value={filters.sido} onChange={(e) => setFilters({ ...filters, sido: e.target.value })} placeholder="지역 (예: 경북) — 희망 시·도 미선택자는 포함" />
            <select value="" onChange={(e) => e.target.value && setFilters({ ...filters, denominations: [...new Set([...filters.denominations, e.target.value])] })}>
              <option value="">교단 추가 (비우면 전체)</option>
              {DENOMINATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            {filters.denominations.map((d) => (
              <button key={d} type="button" onClick={() => setFilters({ ...filters, denominations: filters.denominations.filter((x) => x !== d) })}>{d} ✕</button>
            ))}
          </div>
          <p>대상 <b>{preview ? preview.length : '...'}</b>명 {preview && preview.length > 0 && <small>{preview.slice(0, 12).map((p) => p.realName).join(', ')}{preview.length > 12 ? ' …' : ''}</small>}</p>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2} placeholder="이메일에 함께 보낼 한마디 (선택)" />
          <div className="service-detail-overview__toolbar">
            <button type="button" onClick={() => setTarget(null)}>취소</button>
            <button type="button" className="is-primary" disabled={busy || !preview?.length} onClick={send}>발송</button>
          </div>
        </div>
      )}

      <h4>홍보할 수 있는 공고·의뢰 ({rows.length})</h4>
      {rows.length === 0 ? <div className="service-detail-overview__empty">지금 열려 있는 담임 공고나 홍보 동의 의뢰가 없습니다.</div> : (
        <div className="service-detail-overview__table-x">
          <table className="service-detail-overview__table">
            <thead><tr><th>구분</th><th>제목</th><th>교회</th><th>지역</th><th>등록</th><th>홍보 이력</th><th /></tr></thead>
            <tbody>
              {rows.map((s) => (
                <tr key={`${s.sourceType}-${s.recruitType}-${s.id}`}>
                  <td>{s.sourceType === 'RECRUIT' ? '담임 공고' : '청빙 의뢰'}</td>
                  <td><a href={s.path} target="_blank" rel="noreferrer">{s.title}</a></td>
                  <td>{s.church}</td>
                  <td>{s.region}</td>
                  <td>{s.postedAt}</td>
                  <td>{s.sent ? `${s.sent.count}회 · ${s.sent.last?.slice(0, 10)}` : '-'}</td>
                  <td className="pastor-admin__actions">
                    <button type="button" onClick={() => choose(s)}>홍보</button>
                    {s.sourceType === 'RECRUIT'
                      ? <button type="button" onClick={() => toRequest(s)}>의뢰로 만들기</button>
                      : <button type="button" onClick={() => onOpenRequest(s.id)}>의뢰 보기</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h4>발송 이력</h4>
      {history.length === 0 ? <div className="service-detail-overview__empty">발송 이력이 없습니다.</div> : (
        <div className="service-detail-overview__table-x">
          <table className="service-detail-overview__table">
            <thead><tr><th>발송일</th><th>구분</th><th>제목</th><th>교회·지역</th><th>인원</th><th>한마디</th></tr></thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id}>
                  <td>{h.createdAt}</td>
                  <td>{h.sourceType === 'RECRUIT' ? '담임 공고' : '청빙 의뢰'}</td>
                  <td>{h.title}</td>
                  <td>{[h.church, h.region].filter(Boolean).join(' · ')}</td>
                  <td>{h.recipientCount}</td>
                  <td>{h.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------------
// 사역자 이메일 홍보 — 공고·이력서 등록 이메일을 한 목록으로 모아 담임청빙 중개 안내 (server pastorMail.js)
// ---------------------------------------------------------------------------------
type MailStatus = 'READY' | 'FAILED' | 'SENT' | 'REGISTERED' | 'OPTOUT';
type MailContent = { subject: string; heading: string; body: string; ctaLabel: string };
type MailRecipient = { email: string; name: string; sources: string[]; since: string; status: MailStatus; sentAt: string | null; error: string };
type MailJob = { running: boolean; stopping: boolean; total: number; sent: number; failed: number; startedAt: string | null; finishedAt: string | null; lastError: string };

const MAIL_STATUS_LABEL: Record<MailStatus, string> = { READY: '발송 대상', FAILED: '실패', SENT: '보냄', REGISTERED: '이미 등록', OPTOUT: '수신거부' };
const UNSENT_STATUSES: MailStatus[] = ['READY', 'FAILED', 'REGISTERED', 'OPTOUT'];
const MAIL_SEND_MAX = 500;
const MAIL_LIST_PAGE = 200;

function MailTable({ rows, shown, onMore, sent }: { rows: MailRecipient[]; shown: number; onMore: () => void; sent?: boolean }) {
  if (rows.length === 0) return <div className="service-detail-overview__empty">{sent ? '보낸 메일이 없습니다.' : '해당하는 이메일이 없습니다.'}</div>;
  return (
    <>
      <div className="service-detail-overview__table-x">
        <table className="service-detail-overview__table">
          <thead><tr>{sent && <th>발송일</th>}<th>등록일</th><th>이메일</th><th>교회·이름</th><th>출처</th>{!sent && <th>상태</th>}</tr></thead>
          <tbody>
            {rows.slice(0, shown).map((it) => (
              <tr key={it.email}>
                {sent && <td>{it.sentAt || '-'}</td>}
                <td>{it.since || '-'}</td>
                <td>{it.email}</td>
                <td>{it.name}</td>
                <td>{it.sources.join(', ')}</td>
                {!sent && <td>{MAIL_STATUS_LABEL[it.status]}{it.error && <><br /><small>{it.error}</small></>}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > shown && (
        <div className="service-detail-overview__toolbar">
          <button type="button" onClick={onMore}>더 보기 ({(rows.length - shown).toLocaleString()}건 남음)</button>
        </div>
      )}
    </>
  );
}

function MailPromo() {
  const [content, setContent] = useState<MailContent | null>(null);
  const [ctaHref, setCtaHref] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [data, setData] = useState<{ items: MailRecipient[]; counts: Record<MailStatus, number> } | null>(null);
  const [filter, setFilter] = useState<MailStatus | ''>('READY');
  const [keyword, setKeyword] = useState('');
  const [shown, setShown] = useState(MAIL_LIST_PAGE);
  const [sentShown, setSentShown] = useState(MAIL_LIST_PAGE);
  const [listTab, setListTab] = useState<'unsent' | 'sent'>('unsent');
  const [testTo, setTestTo] = useState('');
  const [limit, setLimit] = useState('100');
  const [includeFailed, setIncludeFailed] = useState(false);
  const [job, setJob] = useState<MailJob | null>(null);
  const [history, setHistory] = useState<{ subject: string; day: string; sent: number; failed: number }[]>([]);
  const [busy, setBusy] = useState(false);

  const loadTemplate = useCallback(() => {
    setContent(null);
    api.get('/mail/template').then((r) => {
      const { subject, heading, body, ctaLabel } = r.data;
      setContent({ subject, heading, body, ctaLabel });
      setCtaHref(r.data.ctaHref);
    }).catch((e) => alert(err(e, '기본 문구를 불러오지 못했습니다.')));
  }, []);

  const loadRecipients = useCallback(() => {
    setData(null);
    api.get('/mail/recipients').then((r) => setData(r.data)).catch((e) => {
      alert(err(e, '이메일 목록을 불러오지 못했습니다.'));
      setData({ items: [], counts: { READY: 0, FAILED: 0, SENT: 0, REGISTERED: 0, OPTOUT: 0 } });
    });
    api.get('/mail/history').then((r) => setHistory(r.data.items)).catch(() => setHistory([]));
  }, []);

  useEffect(() => {
    loadTemplate();
    loadRecipients();
  }, [loadTemplate, loadRecipients]);

  // 미리보기는 입력이 멈춘 뒤 서버에서 실제 메일 본문으로 만든다
  useEffect(() => {
    if (!content) return undefined;
    const t = window.setTimeout(() => {
      api.post('/mail/preview', { content }).then((r) => setPreviewHtml(r.data.html)).catch(() => {});
    }, 400);
    return () => window.clearTimeout(t);
  }, [content]);

  // 발송 진행 상황
  const running = Boolean(job?.running);
  useEffect(() => {
    let alive = true;
    const poll = () => api.get('/mail/job').then((r) => alive && setJob(r.data.job)).catch(() => {});
    poll();
    const t = window.setInterval(poll, running ? 3000 : 15000);
    return () => { alive = false; window.clearInterval(t); };
  }, [running]);

  const wasRunning = useRef(false);
  useEffect(() => {
    if (wasRunning.current && !running) loadRecipients();
    wasRunning.current = running;
  }, [running, loadRecipients]);

  const sendTest = async () => {
    if (!content) return;
    setBusy(true);
    try {
      await api.post('/mail/test', { to: testTo, content });
      alert(`${testTo} 으로 테스트 메일을 보냈습니다.\n받은편지함에 없으면 스팸함을 확인해 주세요.`);
    } catch (e) {
      alert(err(e, '테스트 메일을 보내지 못했습니다.'));
    } finally {
      setBusy(false);
    }
  };

  const ready = (data?.counts.READY || 0) + (includeFailed ? data?.counts.FAILED || 0 : 0);
  const count = Math.min(ready, MAIL_SEND_MAX, Math.max(0, Number(limit) || 0));

  const send = async () => {
    if (!content || !count) return;
    if (!window.confirm(`${count}건에 홍보 메일을 보낼까요?\n\n제목: ${content.subject}\n\n서버가 오래된 주소부터 순서대로 보냅니다. 화면을 닫아도 계속 보내며, 결과는 '보낸 메일' 탭에서 확인하세요.`)) return;
    setBusy(true);
    try {
      const r = await api.post('/mail/send', { content, limit: count, includeFailed });
      setJob(r.data.job);
    } catch (e) {
      alert(err(e, '발송을 시작하지 못했습니다.'));
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    try {
      const r = await api.post('/mail/stop');
      setJob(r.data.job);
    } catch (e) {
      alert(err(e));
    }
  };

  const kw = keyword.trim().toLowerCase();
  const match = (it: MailRecipient) => !kw || it.email.includes(kw) || it.name.toLowerCase().includes(kw);
  const items = data?.items || [];
  const unsent = items.filter((it) => it.status !== 'SENT' && (!filter || it.status === filter) && match(it));
  const sentRows = items.filter((it) => it.status === 'SENT' && match(it));

  return (
    <>
      <p>공고 등록 이메일(수집 이메일·구인 공고 문의 이메일)과 이력서 등록 이메일(이력서·프로필)을 구분 없이 모아 담임목사청빙지원을 안내합니다. 한 주소에는 한 번만 보냅니다.</p>
      <p>
        {data ? (Object.keys(MAIL_STATUS_LABEL) as MailStatus[]).map((k) => `${MAIL_STATUS_LABEL[k]} ${data.counts[k].toLocaleString()}`).join(' · ') : '이메일을 모으는 중...'}
      </p>

      {/* 발송은 서버가 알아서 한다 — 화면에는 진행 중인지만 알리고, 결과는 '보낸 메일' 탭에서 본다 */}
      {job?.running && (
        <div className="service-detail-overview__toolbar">
          <span className="pastor-admin__warn">{job.stopping ? '발송을 멈추는 중입니다.' : '서버에서 발송 중입니다. 화면을 닫아도 계속 보냅니다.'}</span>
          {!job.stopping && <button type="button" onClick={stop}>발송 중지</button>}
        </div>
      )}

      <div className="pastor-admin__cols">
        <div>
          <h4>메일 내용</h4>
          {!content ? <div className="service-detail-overview__empty">불러오는 중...</div> : (
            <div className="pastor-admin__form">
              <label>제목<input value={content.subject} onChange={(e) => setContent({ ...content, subject: e.target.value })} maxLength={200} style={{ width: '100%', boxSizing: 'border-box' }} /></label>
              <label>큰 제목<input value={content.heading} onChange={(e) => setContent({ ...content, heading: e.target.value })} maxLength={100} style={{ width: '100%', boxSizing: 'border-box' }} /></label>
              <label>본문 <small>(큰 제목 아래 문단, 줄마다 한 문단 — 그 아래 안내 내용은 고정입니다)</small><textarea value={content.body} onChange={(e) => setContent({ ...content, body: e.target.value })} rows={7} maxLength={3000} /></label>
              <label>버튼 문구 <small>→ {ctaHref}</small><input value={content.ctaLabel} onChange={(e) => setContent({ ...content, ctaLabel: e.target.value })} maxLength={40} style={{ width: '100%', boxSizing: 'border-box' }} /></label>
              <div className="service-detail-overview__toolbar">
                <button type="button" onClick={loadTemplate}>기본 문구로 되돌리기</button>
              </div>

              <h4>테스트 발송</h4>
              <div className="pastor-admin__send">
                <input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="테스트 받을 이메일" style={{ flex: 1 }} />
                <button type="button" disabled={busy || !testTo.trim()} onClick={sendTest}>테스트 발송</button>
              </div>

              <h4>홍보 발송</h4>
              <div className="pastor-admin__send">
                <label className="pastor-admin__check">이번에 보낼 건수
                  <input type="number" min={1} max={MAIL_SEND_MAX} value={limit} onChange={(e) => setLimit(e.target.value)} style={{ width: 100 }} />
                </label>
                <label className="pastor-admin__check">
                  <input type="checkbox" checked={includeFailed} onChange={(e) => setIncludeFailed(e.target.checked)} /> 실패한 주소 다시 보내기
                </label>
                <button type="button" className="is-primary" disabled={busy || running || !count} onClick={send}>{count.toLocaleString()}건 발송</button>
              </div>
              <small>버튼을 누르면 서버가 발송 대상 중 오래된 순으로 1초 간격으로 보냅니다 (한 번에 최대 {MAIL_SEND_MAX}건). 화면을 닫아도 계속 보내고, 결과는 '보낸 메일' 탭에서 확인합니다. 보낸 주소·수신거부(기존 공고 수신거부 포함)·이미 담임 이력서를 맡긴 주소는 빠집니다.</small>
            </div>
          )}
        </div>
        <div>
          <h4>미리보기</h4>
          {previewHtml
            ? <iframe title="메일 미리보기" srcDoc={previewHtml} style={{ width: '100%', height: 620, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff' }} />
            : <div className="service-detail-overview__empty">미리보기를 만드는 중...</div>}
        </div>
      </div>

      <div className="service-detail-overview__toolbar" style={{ marginTop: 28 }}>
        <input className="pastor-admin__search" value={keyword} onChange={(e) => { setKeyword(e.target.value); setShown(MAIL_LIST_PAGE); setSentShown(MAIL_LIST_PAGE); }} placeholder="이메일·교회·이름 검색" />
        <button type="button" onClick={loadRecipients}>새로고침</button>
      </div>

      <div className="service-detail-overview__tabs" role="tablist">
        {([['unsent', `보낼 메일 (${unsent.length.toLocaleString()})`], ['sent', `보낸 메일 (${sentRows.length.toLocaleString()})`]] as const).map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={listTab === k}
            className={`service-detail-overview__tab${listTab === k ? ' is-active' : ''}`}
            onClick={() => setListTab(k)}>
            {label}
          </button>
        ))}
      </div>
      {listTab === 'unsent' ? (
        <>
          <div className="service-detail-overview__toolbar">
            <select value={filter} onChange={(e) => { setFilter(e.target.value as MailStatus | ''); setShown(MAIL_LIST_PAGE); }}>
              <option value="">전체 (보낸 메일 제외)</option>
              {UNSENT_STATUSES.map((k) => <option key={k} value={k}>{MAIL_STATUS_LABEL[k]}</option>)}
            </select>
            <small>오래된 순 — 이 순서대로 발송됩니다</small>
          </div>
          {!data ? <div className="service-detail-overview__empty">불러오는 중...</div> : <MailTable rows={unsent} shown={shown} onMore={() => setShown(shown + MAIL_LIST_PAGE)} />}
        </>
      ) : (
        <>
          <div className="service-detail-overview__toolbar"><small>최근 발송 순</small></div>
          {!data ? <div className="service-detail-overview__empty">불러오는 중...</div> : <MailTable sent rows={sentRows} shown={sentShown} onMore={() => setSentShown(sentShown + MAIL_LIST_PAGE)} />}
        </>
      )}

      <h4>발송 이력</h4>
      {history.length === 0 ? <div className="service-detail-overview__empty">발송 이력이 없습니다.</div> : (
        <div className="service-detail-overview__table-x">
          <table className="service-detail-overview__table">
            <thead><tr><th>날짜</th><th>제목</th><th>보냄</th><th>실패</th></tr></thead>
            <tbody>
              {history.map((h) => (
                <tr key={`${h.day}-${h.subject}`}>
                  <td>{h.day}</td>
                  <td>{h.subject}</td>
                  <td>{h.sent}</td>
                  <td>{h.failed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------------
// 상담 신청
// ---------------------------------------------------------------------------------
const CONSULT_STATUS: Record<string, string> = { NEW: '신규', CONTACTED: '연락함', DONE: '완료' };

function Consults() {
  const [items, setItems] = useState<{ id: number; churchName: string; contactName: string; phone: string; region: string; memo: string; status: string; createdAt: string }[] | null>(null);
  const load = useCallback(() => {
    api.get('/consults').then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, []);
  useEffect(load, [load]);

  const update = async (id: number, status: string) => {
    try {
      await api.patch(`/consults/${id}`, { status });
      load();
    } catch (e) {
      alert(err(e));
    }
  };

  if (!items) return <div className="service-detail-overview__empty">불러오는 중...</div>;
  return items.length === 0 ? <div className="service-detail-overview__empty">상담 신청이 없습니다.</div> : (
    <div className="service-detail-overview__table-x">
      <table className="service-detail-overview__table">
        <thead><tr><th>신청일</th><th>교회</th><th>담당자</th><th>연락처</th><th>지역</th><th>내용</th><th>상태</th></tr></thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id}>
              <td>{c.createdAt}</td>
              <td><b>{c.churchName}</b></td>
              <td>{c.contactName}</td>
              <td>{c.phone}</td>
              <td>{c.region}</td>
              <td className="pc-pre">{c.memo}</td>
              <td>
                <select value={c.status} onChange={(e) => update(c.id, e.target.value)}>
                  {Object.entries(CONSULT_STATUS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
